/**
 * The Get Started checklist: the member's six personal steps and the firm
 * items they can see, derived on the server from what is actually filled in
 * (`GET /api/onboarding`), so it is never out of step with Settings. Floats
 * bottom right on the onboarding page and follows the member around the app
 * until they finish or dismiss it. Collapses to a pill.
 */
import { ChevronDown, ChevronRight, ChevronUp, Lock, X } from "lucide-react";
import { useState, type FC } from "react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import { firmItemCopy, openFirmItems } from "../firmItems";
import type {
  FirmSetupItem,
  OnboardingProgress,
  OnboardingStep
} from "../types";
import { OpenSettingsButton } from "./OpenSettingsButton";
import { StepDot, type StepState } from "./StepDot";

const COLLAPSED_KEY = "caseblink.onboarding.checklistCollapsed";

const readCollapsed = (fallback: boolean): boolean => {
  try {
    const raw = window.localStorage.getItem(COLLAPSED_KEY);
    return raw === null ? fallback : raw === "1";
  } catch {
    return fallback;
  }
};

const writeCollapsed = (value: boolean) => {
  try {
    window.localStorage.setItem(COLLAPSED_KEY, value ? "1" : "0");
  } catch {
    // Private mode: the choice lasts for this page only.
  }
};

/** Each step's state; the first open step is the current one. */
export const stepStates = (steps: readonly OnboardingStep[]): StepState[] => {
  let currentSeen = false;
  return steps.map((s) => {
    if (s.done) return "done";
    if (s.skipped) return "skipped";
    if (!currentSeen) {
      currentSeen = true;
      return "current";
    }
    return "open";
  });
};

export const isComplete = (progress: OnboardingProgress): boolean =>
  progress.status === "completed" ||
  (progress.steps.length > 0 &&
    progress.steps.every((s) => s.done || s.skipped));

const ProgressRing: FC<{ value: number }> = ({ value }) => {
  const r = 6;
  const c = 2 * Math.PI * r;
  return (
    <svg
      viewBox="0 0 16 16"
      className="size-4 shrink-0 -rotate-90"
      aria-hidden
      data-testid="checklist-ring"
    >
      <circle
        cx="8"
        cy="8"
        r={r}
        fill="none"
        strokeWidth={2}
        className="stroke-fill-3"
      />
      <circle
        cx="8"
        cy="8"
        r={r}
        fill="none"
        strokeWidth={2}
        strokeLinecap="round"
        strokeDasharray={`${c * value} ${c}`}
        className="stroke-accent"
      />
    </svg>
  );
};

type Props = {
  progress: OnboardingProgress;
  /** A personal step was clicked (not shown for done steps). */
  onStep?: (step: OnboardingStep) => void;
  onFirmItem?: (item: FirmSetupItem) => void;
  onDismiss?: () => void;
  defaultCollapsed?: boolean;
  className?: string;
};

export const GetStartedChecklist: FC<Props> = ({
  progress,
  onStep,
  onFirmItem,
  onDismiss,
  defaultCollapsed = false,
  className
}) => {
  const [collapsed, setCollapsedState] = useState(() =>
    readCollapsed(defaultCollapsed)
  );
  const setCollapsed = (value: boolean) => {
    setCollapsedState(value);
    writeCollapsed(value);
  };
  const { steps } = progress;
  const total = steps.length || progress.total;
  const finished = Math.min(
    steps.filter((s) => s.done || s.skipped).length,
    total
  );
  const pct = total ? finished / total : 0;
  const complete = isComplete(progress);
  const states = stepStates(steps);
  const firmOpen = openFirmItems(progress.firm_items);

  if (collapsed) {
    return (
      <button
        type="button"
        onClick={() => setCollapsed(false)}
        className={cn(
          "inline-flex h-8 items-center gap-2 rounded-full bg-elevated px-2.5 text-base text-fg shadow-popover ring-1 ring-line-2",
          className
        )}
        data-testid="get-started-pill"
      >
        <ProgressRing value={pct} />
        <span className="font-medium">
          {complete ? "You're All Set" : "Get Started"}
        </span>
        <span className="text-fg-3 tabular-nums">
          {finished}/{total}
        </span>
        <ChevronUp className="size-3.5 text-fg-3" />
      </button>
    );
  }

  return (
    <div
      className={cn(
        "w-66 rounded-xl bg-elevated text-base text-fg shadow-popover ring-1 ring-line-2",
        className
      )}
      data-testid="get-started-checklist"
      data-complete={complete ? "" : undefined}
    >
      <div className="flex items-center gap-1.5 pt-2.5 pr-2 pl-3">
        <span className="flex-1 font-medium">
          {complete ? "You're All Set" : "Get Started"}
        </span>
        <Button
          variant="ghost"
          size="icon-xs"
          aria-label="Collapse the checklist"
          onClick={() => setCollapsed(true)}
        >
          <ChevronDown />
        </Button>
        {onDismiss ? (
          <Button
            variant="ghost"
            size="icon-xs"
            aria-label="Dismiss the checklist"
            onClick={onDismiss}
            data-testid="get-started-dismiss"
          >
            <X />
          </Button>
        ) : null}
      </div>
      <div className="px-3 pt-0.5 pb-2">
        <div className="mb-1.5 text-sm text-fg-3 tabular-nums">
          {complete
            ? "Everything's ready."
            : `${finished} of ${total} Complete`}
        </div>
        <div className="h-1 overflow-hidden rounded-full bg-fill-2">
          <div
            className={cn(
              "h-full rounded-full transition-[width] duration-400",
              complete ? "bg-success" : "bg-accent"
            )}
            style={{ width: `${pct * 100}%` }}
          />
        </div>
      </div>
      <ul className="flex flex-col px-1.5 pb-1.5">
        {steps.map((step, i) => {
          const state = states[i];
          const current = state === "current";
          const clickable = Boolean(onStep) && state !== "done";
          return (
            <li key={step.id}>
              <button
                type="button"
                disabled={!clickable}
                onClick={() => onStep?.(step)}
                className={cn(
                  "flex h-7.5 w-full items-center gap-2 rounded-md px-1.5 text-left",
                  current && "bg-fill-1",
                  clickable && "hover:bg-fill-1"
                )}
                data-testid={`checklist-step-${step.id}`}
                data-state={state}
              >
                <StepDot state={state} />
                <span
                  className={cn(
                    "flex-1 truncate",
                    state === "done" && "text-fg-4 line-through",
                    state === "skipped" && "text-fg-4",
                    current && "font-medium text-fg",
                    state === "open" && "text-fg-2"
                  )}
                >
                  {step.label}
                </span>
                {state === "skipped" ? (
                  <span className="text-sm text-fg-4">Skipped</span>
                ) : null}
                {current ? <ChevronRight className="size-3 text-fg-4" /> : null}
              </button>
            </li>
          );
        })}
      </ul>
      {firmOpen.length ? (
        <div className="border-t border-line-1 px-1.5 pt-2 pb-1.5">
          <div className="flex justify-between px-1.5 pb-1 text-sm text-fg-3">
            <span>Firm Setup</span>
            <span className="tabular-nums">{firmOpen.length} Open</span>
          </div>
          <ul>
            {firmOpen.map((item) => {
              const copy = firmItemCopy(item.id);
              const Icon = copy.icon;
              const locked = !item.can_edit;
              return (
                <li key={item.id} className="flex items-center gap-0.5">
                  <button
                    type="button"
                    disabled={locked || !onFirmItem}
                    onClick={() => onFirmItem?.(item)}
                    className={cn(
                      "flex h-7.5 w-full min-w-0 items-center gap-2 rounded-md px-1.5 text-left",
                      !locked && onFirmItem && "hover:bg-fill-1"
                    )}
                    title={
                      locked && item.ask
                        ? `For ${item.ask} or another admin`
                        : undefined
                    }
                    data-testid={`checklist-firm-${item.id}`}
                    data-locked={locked ? "" : undefined}
                  >
                    <Icon
                      className={cn(
                        "size-3.5 shrink-0",
                        locked ? "text-fg-4" : "text-fg-3"
                      )}
                    />
                    <span
                      className={cn(
                        "flex-1 truncate",
                        locked ? "text-fg-4" : "text-fg-2"
                      )}
                    >
                      {item.label}
                    </span>
                    {locked ? (
                      <Lock className="size-3 text-fg-4" />
                    ) : (
                      <ChevronRight className="size-3 text-fg-4" />
                    )}
                  </button>
                  {copy.settingsSection && !locked ? (
                    <OpenSettingsButton
                      section={copy.settingsSection}
                      label={`Open ${item.label} Settings`}
                      iconOnly
                      testId={`checklist-firm-settings-${item.id}`}
                    />
                  ) : null}
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
    </div>
  );
};
